/**
 * DMV Soundstage Analytics Tracker
 * Privacy-friendly analytics with opt-out support
 */

import { API } from '../config/api';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('dmv_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('dmv_session_id', sessionId);
  }
  return sessionId;
};

// Get device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Get browser name
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Other';
};

// Parse UTM parameters
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
  };
};

// Check if user has opted out
const isOptedOut = () => {
  return localStorage.getItem('dmv_analytics_optout') === 'true';
};

// Event queue for batching
let eventQueue = [];
let flushTimeout = null;

// Flush events to server
const flushEvents = async () => {
  if (eventQueue.length === 0) return;
  
  const events = [...eventQueue];
  eventQueue = [];
  
  try {
    const response = await fetch(`${API}/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ events }),
    });
    
    if (!response.ok) {
      // Re-queue failed events
      eventQueue = [...events, ...eventQueue];
      console.warn('[Analytics] Failed to flush events, will retry');
    }
  } catch (err) {
    // Re-queue on network error
    eventQueue = [...events, ...eventQueue];
    console.warn('[Analytics] Network error, will retry');
  }
};

// Schedule flush
const scheduleFlush = () => {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(flushEvents, 2000); // Batch events for 2 seconds
};

// Track an event
export const trackEvent = (eventType, data = {}) => {
  // Don't track if opted out (but allow anonymous aggregates)
  const optedOut = isOptedOut();
  
  const event = {
    event_type: eventType,
    path: window.location.pathname,
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    referrer: document.referrer || null,
    device: getDeviceType(),
    browser: getBrowser(),
    ...getUTMParams(),
    // Only include user_id if not opted out
    user_id: optedOut ? null : data.user_id,
    // Additional data
    ...data,
    // Mark as anonymous if opted out
    is_anonymous: optedOut,
  };
  
  eventQueue.push(event);
  scheduleFlush();
  
  console.log('[Analytics] Event tracked:', eventType, window.location.pathname);
};

// Track page view
export const trackPageView = (userId = null) => {
  trackEvent('page_view', { user_id: userId });
};

// Track request button click
export const trackRequestClick = (contentType, contentId, userId = null) => {
  trackEvent('request_click', {
    content_type: contentType,
    content_id: contentId,
    user_id: userId,
  });
  console.log(`[Analytics] Request click tracked: ${contentType} ${contentId}`);
};

// Track request submission
export const trackRequestSubmit = (contentType, contentId, success, userId = null) => {
  trackEvent('request_submit', {
    content_type: contentType,
    content_id: contentId,
    success,
    user_id: userId,
  });
  console.log(`[Analytics] Request submit tracked: ${contentType} ${contentId} - success: ${success}`);
};

// Track donation
export const trackDonation = (amount, isRecurring, userId = null) => {
  trackEvent('donation', {
    amount,
    is_recurring: isRecurring,
    user_id: userId,
  });
};

// Track profile view
export const trackProfileView = (profileId, profileType, userId = null) => {
  trackEvent('profile_view', {
    profile_id: profileId,
    profile_type: profileType,
    user_id: userId,
  });
};

// Set opt-out preference
export const setAnalyticsOptOut = (optOut) => {
  localStorage.setItem('dmv_analytics_optout', optOut ? 'true' : 'false');
};

// Get opt-out preference
export const getAnalyticsOptOut = () => {
  return isOptedOut();
};

// Initialize analytics on page load
export const initAnalytics = (userId = null) => {
  // Track initial page view
  trackPageView(userId);
  
  // Flush on page unload
  window.addEventListener('beforeunload', flushEvents);
  
  // Flush on visibility change (when user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushEvents();
    }
  });
};

export default {
  trackEvent,
  trackPageView,
  trackRequestClick,
  trackRequestSubmit,
  trackDonation,
  trackProfileView,
  setAnalyticsOptOut,
  getAnalyticsOptOut,
  initAnalytics,
};
